import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { LogIn, User, Download, Upload, Moon, Sun, LogOut, Home, Store } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useSettingsStore } from '@/stores/useSettingsStore';
import { useThemeEffect } from '@/hooks/useThemeEffect';
import AuthSignIn from './AuthSignIn';
import AuthSignUp from './AuthSignUp';
import AuthResetPassword from './AuthResetPassword';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export type AuthView = 'signIn' | 'signUp' | 'resetPassword';

const AuthPopover = () => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<AuthView>('signUp');
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { displayName, avatarUrl } = useUserProfile();
  const { settings, setTheme } = useSettingsStore();
  const { isDark } = useThemeEffect();

  const handleClose = () => {
    setOpen(false);
    // Reset to sign up view when closing
    setTimeout(() => setView('signUp'), 300);
  };

  const handleViewChange = (newView: AuthView) => {
    setView(newView);
  };

  const handleSignOut = async () => {
    await logout();
    navigate('/');
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  const handleMySequencesClick = () => {
    navigate('/my-sequences');
  };

  const handleDownloadHistoryClick = () => {
    navigate('/my-downloads');
  };

  const handleSellerDashboardClick = () => {
    navigate('/seller-dashboard');
  };

  const toggleDarkMode = () => {
    setTheme(isDark ? 'light' : 'dark');
  };

  if (user) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="rounded-full px-5">
            <User size={18} className="mr-2" />
            Profile
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[280px] p-0">
          {/* Account Header */}
          <div className="p-4 border-b">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                {avatarUrl ? (
                  <img 
                    src={avatarUrl} 
                    alt={displayName}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                ) : (
                  <User className="h-5 w-5 text-primary" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">
                  {displayName}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {user.email}
                </p>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Account</p>
          </div>

          {/* Menu Items */}
          <div className="py-2">
            <DropdownMenuItem onClick={handleProfileClick} className="px-4 py-3 cursor-pointer">
              <Home className="h-4 w-4 mr-3" />
              <span className="font-medium">My Display</span>
            </DropdownMenuItem>
            
            <DropdownMenuItem onClick={handleMySequencesClick} className="px-4 py-3 cursor-pointer">
              <Upload className="h-4 w-4 mr-3" />
              <span className="font-medium">My Sequences</span>
            </DropdownMenuItem>
            
            <DropdownMenuItem onClick={handleDownloadHistoryClick} className="px-4 py-3 cursor-pointer">
              <Download className="h-4 w-4 mr-3" />
              <span className="font-medium">Download History</span>
            </DropdownMenuItem>
            
            <DropdownMenuItem onClick={handleSellerDashboardClick} className="px-4 py-3 cursor-pointer">
              <Store className="h-4 w-4 mr-3" />
              <span className="font-medium">Seller Dashboard</span>
            </DropdownMenuItem>

            <DropdownMenuSeparator className="my-2" />

            {/* Dark Mode Toggle */}
            <div className="px-4 py-3 flex items-center justify-between">
              <div className="flex items-center">
                {isDark ? <Moon className="h-4 w-4 mr-3" /> : <Sun className="h-4 w-4 mr-3" />}
                <span className="font-medium">Dark Mode</span>
              </div>
              <Switch
                checked={isDark}
                onCheckedChange={toggleDarkMode}
                className="data-[state=checked]:bg-primary"
              />
            </div>

            <DropdownMenuSeparator className="my-2" />

            <DropdownMenuItem onClick={handleSignOut} className="px-4 py-3 cursor-pointer text-red-600 focus:text-red-600">
              <LogOut className="h-4 w-4 mr-3" />
              <span className="font-medium">Sign Out</span>
            </DropdownMenuItem>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button onClick={() => setOpen(true)} className="rounded-full px-5">
        <LogIn size={18} className="mr-2" />
        Sign Up
      </Button>
      <DialogContent className="sm:max-w-[425px]">
        {view === 'signIn' && (
          <AuthSignIn 
            onViewChange={handleViewChange} 
            onSuccess={() => {
              handleClose();
              navigate('/');
            }}
          />
        )}
        {view === 'signUp' && (
          <AuthSignUp 
            onViewChange={handleViewChange} 
            onSuccess={() => {
              handleClose();
              navigate('/');
            }}
          />
        )}
        {view === 'resetPassword' && (
          <AuthResetPassword 
            onViewChange={handleViewChange}
            onSuccess={() => {
              handleClose();
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuthPopover;
